/* RESPONSIBLE TEAM: team-proactive-support */
import { isPresent } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import Node, { type Inputs as NodeInputs } from './node';
import Edge, { type Inputs as EdgeInputs } from './edge';
import { type ActionPerformed, AddNode, DeleteNode, AddEdge, DeleteEdge } from './action';
import Settings from './settings';
import State from './state';
import Hooks, { type GraphHooksInterface } from './hooks';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed } from '@ember/object';
import { A } from '@ember/array';
import type MutableArray from '@ember/array/mutable';
import Coordinates from './coordinates';
import Offset, { type SimpleOffset } from './offset';
import type ConnectionPoint from './connection-point';

export default class Graph<
  NodeDataObjectType = any,
  EdgeDataObjectType = any,
  ConnectionPointDataObjectType = any,
> {
  @tracked element?: HTMLElement;
  @tracked wrapper?: HTMLElement;
  @tracked nodes: MutableArray<Node<NodeDataObjectType>> = A();
  @tracked edges: MutableArray<Edge<EdgeDataObjectType>> = A();
  @tracked settings: Settings;
  @tracked state: State;

  @tracked actionsToUndo: MutableArray<MutableArray<ActionPerformed>> = A();
  @tracked actionsToRedo: MutableArray<MutableArray<ActionPerformed>> = A();
  recordingActions = false;

  hooks: Hooks;

  constructor(hooks?: GraphHooksInterface) {
    this.hooks = new Hooks(hooks);
    this.state = new State(this);
    this.settings = new Settings();
  }

  @action setElement(element: HTMLElement): void {
    this.element = element;
  }

  @computed(
    'element',
    'state.gridOffset.{x,y}',
    'state.scale',
    'state.scaleTranslate.{x,y}',
    'state.wrapperBoundingRect.{x,y}',
  )
  get elementRect(): DOMRect {
    return this.element?.getBoundingClientRect() ?? new DOMRect(0, 0, 0, 0);
  }

  @action setWrapper(element: HTMLElement): void {
    this.wrapper = element;
  }

  @action setSelectedEdge(edge: Edge<EdgeDataObjectType>) {
    this.state.selectedEdge = edge;
  }

  @action deselectEdge() {
    this.state.selectedEdge = undefined;
  }

  trackActions(action: () => any) {
    this.startTrackingActions();

    action();

    this.stopTrackingActions();
  }

  startTrackingActions() {
    this.recordingActions = true;
    this.actionsToRedo = A();
    this.actionsToUndo.pushObject(A());
  }

  stopTrackingActions() {
    if (this.actionsToUndo.lastObject?.length === 0) {
      this.actionsToUndo.popObject();
    }
    this.recordingActions = false;
  }

  recordAction(action: ActionPerformed) {
    if (this.recordingActions) {
      let latestAction = this.actionsToUndo.popObject()!;
      latestAction.pushObject(action);
      this.actionsToUndo.pushObject(latestAction);
    }
  }

  clearRecordedActions() {
    this.actionsToUndo = A();
    this.actionsToRedo = A();
  }

  undoAction() {
    if (this.recordingActions) {
      return;
    }
    let latestAction = this.actionsToUndo.popObject();
    if (latestAction) {
      let action = latestAction.popObject();
      let actionsUndone: MutableArray<ActionPerformed> = A();
      try {
        while (action) {
          action.undo();
          actionsUndone.pushObject(action);
          action = latestAction.popObject();
        }
        this.actionsToRedo.pushObject(actionsUndone);
      } catch (e) {
        latestAction.pushObject(action);
        action = actionsUndone.popObject();
        while (action) {
          action.redo();
          latestAction.pushObject(action);
          action = actionsUndone.popObject();
        }
        this.actionsToUndo.pushObject(latestAction);
      }
    }
  }

  redoAction() {
    if (this.recordingActions) {
      return;
    }
    let latestAction = this.actionsToRedo.popObject();
    if (latestAction) {
      let action = latestAction.popObject();
      let actionsRedone: MutableArray<ActionPerformed> = A();
      try {
        while (action) {
          action.redo();
          actionsRedone.pushObject(action);
          action = latestAction.popObject();
        }
        this.actionsToUndo.pushObject(actionsRedone);
      } catch (e) {
        latestAction.pushObject(action);
        action = actionsRedone.popObject();
        while (action) {
          action.undo();
          latestAction.pushObject(action);
          action = actionsRedone.popObject();
        }
        this.actionsToRedo.pushObject(latestAction);
      }
    }
  }

  addNode(inputs: NodeInputs<NodeDataObjectType>): Node<NodeDataObjectType> {
    let position = inputs.position ?? { x: 0, y: 0 };
    let dataObject = inputs.dataObject;
    let connectionPoints = inputs.connectionPoints ?? [
      { x: 0, y: 0, alignment: 'top', direction: 'both' },
    ];
    let node = new Node(new Coordinates(position.x, position.y), this, dataObject);
    connectionPoints.forEach((connectionPoint) => node.addConnectionPoint(connectionPoint));
    this.nodes.pushObject(node);
    this.recordAction(new AddNode(node));
    return node;
  }

  deleteNode(node: Node<NodeDataObjectType>): boolean {
    if (isPresent(node.edges) && node.edges.some((edge) => !this.hooks.canDeleteEdge(edge))) {
      return false;
    } else if (this.hooks.canDeleteNode(node)) {
      node.edges.forEach((edge) => this.deleteEdge(edge));
      this.nodes.removeObject(node);
      this.recordAction(new DeleteNode(node));
      this.hooks.didDeleteNode(node);
      return true;
    } else {
      return false;
    }
  }

  addEdge(inputs: EdgeInputs<EdgeDataObjectType>): Edge<EdgeDataObjectType> {
    let { predecessor, successor } = inputs;
    let edge = new Edge<EdgeDataObjectType>(predecessor, successor, inputs.dataObject);
    this.edges.pushObject(edge);
    this.recordAction(new AddEdge(edge));
    return edge;
  }

  bringEdgeToForeground(edge: Edge<EdgeDataObjectType>) {
    this.edges.removeObject(edge);
    this.edges.pushObject(edge);
  }

  deleteEdge(edge: Edge<EdgeDataObjectType>): boolean {
    if (this.hooks.canDeleteEdge(edge)) {
      this.edges.removeObject(edge);
      this.state.selectedEdge = undefined;
      this.recordAction(new DeleteEdge(edge));
      this.hooks.didDeleteEdge(edge);
      return true;
    } else {
      return false;
    }
  }

  nodeForDataObject(dataObject: NodeDataObjectType): Node<NodeDataObjectType> | undefined {
    return this.nodes.find((node) => node.dataObject === dataObject);
  }

  connectionPointForDataObject(
    connectionPoints: MutableArray<ConnectionPoint<ConnectionPointDataObjectType>>,
    connectionPointDataObject: ConnectionPointDataObjectType,
  ): ConnectionPoint<ConnectionPointDataObjectType> | undefined {
    return connectionPoints.find(
      (connectionPoint) => connectionPoint.dataObject === connectionPointDataObject,
    );
  }

  edgeForDataObject(dataObject: EdgeDataObjectType): Edge<EdgeDataObjectType> | undefined {
    return this.edges.find((edge) => edge.dataObject === dataObject);
  }

  edgeForNodeDataObjects(
    predecessorDataObject: EdgeDataObjectType,
    successorDataObject: EdgeDataObjectType,
  ): Edge<EdgeDataObjectType> | undefined {
    return this.edges.find(
      (edge) =>
        edge.predecessor.dataObject === predecessorDataObject &&
        edge.successor?.dataObject === successorDataObject,
    );
  }

  setScrollOffset(offset: SimpleOffset): void {
    let element = this.wrapper;
    if (element) {
      element.scrollTop = offset.y;
      element.scrollLeft = offset.x;

      // eslint-disable-next-line ember/no-assignment-of-untracked-properties-used-in-tracking-contexts
      this.state.gridOffset = new Offset(offset.x, offset.y);
    }
  }

  focusNode(node: Node<NodeDataObjectType>, yOffset?: number) {
    let nodeBounds = node.bounds;
    let viewportBounds = this.state.viewportBounds;

    let viewportCenter = viewportBounds.centerPoint;
    let nodeCenter = nodeBounds.centerPoint;

    let differenceX = nodeCenter.x - viewportCenter.x;
    // Take yOffset into account if it exists otherwise centre on node top
    let y = yOffset ? nodeBounds.top + yOffset : nodeCenter.y;
    let differenceY = y - viewportCenter.y;

    differenceX *= this.state.scale;
    differenceY *= this.state.scale;

    let offset = new Offset(
      this.wrapper!.scrollLeft + differenceX,
      this.wrapper!.scrollTop + differenceY,
    );

    this.setScrollOffset(offset);
  }

  findClosestFreeCoordinates(coordinates: Coordinates) {
    let x = Coordinates.roundedCoordinate(coordinates.x, this.settings.grid.spacing);
    let y = Coordinates.roundedCoordinate(coordinates.y, this.settings.grid.spacing);

    while (this.positionOccupied(x, y)) {
      x += this.settings.grid.spacing;
      y += this.settings.grid.spacing * 2;
    }
    return new Coordinates(x, y);
  }

  positionOccupied(xPosition: number, yPosition: number) {
    return this.nodes.any((node) => node.position.x === xPosition && node.position.y === yPosition);
  }

  undeleteNode(node: Node<NodeDataObjectType>, serializedNode: any): Node<NodeDataObjectType> {
    node.dataObject = this.hooks.undeleteNode(serializedNode);
    this.nodes.pushObject(node);
    return node;
  }

  undeleteEdge(edge: Edge<EdgeDataObjectType>, serializedEdge: any): Edge<EdgeDataObjectType> {
    edge.dataObject = this.hooks.undeleteEdge(serializedEdge);
    this.edges.pushObject(edge);
    return edge;
  }
}
